import { fcmService } from '../crud-dashboard/services/fcmService';

const BASE_URL = 'https://fakhama.m-alorfali.com/api';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

export interface Role {
  id: number;
  name: string;
}

export interface User {
  id: number;
  name: string;
  email: string;
  branch_id: number;
  role: string;
  roles?: Role[];
}

export class AuthError extends Error {
  constructor(
    message: string,
    public status?: number,
    public data?: any,
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

class AuthService {
  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_KEY = 'auth_user';

  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await fetch(`${BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        let errorMessage = 'Login failed';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // If parsing fails, use default message
        }
        throw new AuthError(errorMessage, response.status);
      }

      const data: LoginResponse = await response.json();
      
      // Store token
      this.setToken(data.access_token);

      // Fetch user data
      await this.fetchAndStoreUser(data.access_token);

      // Initialize FCM after successful login
      await fcmService.initialize();

      return data;
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }
      throw new AuthError('Network error. Please try again.');
    }
  }

  async fetchAndStoreUser(token: string): Promise<User> {
    try {
      const response = await fetch(`${BASE_URL}/auth/me`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new AuthError('Failed to fetch user data', response.status);
      }

      const user: User = await response.json();

      // Extract role from user.roles[0].name if available
      if (user.roles && user.roles.length > 0) {
        user.role = user.roles[0].name;
      }

      this.setUser(user);
      return user;
    } catch (error) {
      // If fetching user fails, clear auth
      this.clearAuth();
      throw error;
    }
  }

  async logout(): Promise<void> {
    const token = this.getToken();
    
    if (token) {
      try {
        await fetch(`${BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });
      } catch (error) {
        // Ignore logout errors, clear local data anyway
        console.error('Logout error:', error);
      }
    }
    
    this.clearAuth();
  }

  async refreshToken(): Promise<string | null> {
    const token = this.getToken();
    
    if (!token) {
      return null;
    }

    try {
      const response = await fetch(`${BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        this.clearAuth();
        return null;
      }

      const data: LoginResponse = await response.json();
      this.setToken(data.access_token);
      return data.access_token;
    } catch (error) {
      this.clearAuth();
      return null;
    }
  }

  setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  clearToken(): void {
    localStorage.removeItem(this.TOKEN_KEY);
  }

  setUser(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  getUser(): User | null {
    const userStr = localStorage.getItem(this.USER_KEY);
    if (!userStr) {
      return null;
    }
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  clearUser(): void {
    localStorage.removeItem(this.USER_KEY);
  }

  clearAuth(): void {
    this.clearToken();
    this.clearUser();

    // Clear FCM token
    fcmService.clearToken();

    // Dispatch custom event to notify AuthContext in the same tab
    window.dispatchEvent(new CustomEvent('auth-storage-change', {
      detail: { key: 'auth_token', newValue: null, oldValue: null }
    }));
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }
}

export const authService = new AuthService();

